

import React, { useRef, useCallback, useEffect } from "react";
import Workout from "./Workout";
import { useCategoriesInfinite } from "../../hooks/Workout/useCategories";
import {
  useWorkoutsInfinite,
  groupWorkoutsByCategory,
} from "../../hooks/Workout/useWorkouts";

const WorkoutCategory = () => {
  const loaderRef = useRef(null);

  // Use infinite queries with limit 10
  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    isError: categoriesError,
    error: categoriesErr,
    fetchNextPage: fetchNextCategories,
    hasNextPage: hasNextCategories,
    isFetchingNextPage: isFetchingNextCategories,
  } = useCategoriesInfinite(10);

  const {
    data: workoutsData,
    isLoading: workoutsLoading,
    isError: workoutsError,
    error: workoutsErr,
    fetchNextPage: fetchNextWorkouts,
    hasNextPage: hasNextWorkouts,
    isFetchingNextPage: isFetchingNextWorkouts,
  } = useWorkoutsInfinite(10);

  // Flatten all categories and workouts from all pages
  const allCategories = categoriesData?.pages.flatMap(page => page.categories) || [];
  const allWorkouts = workoutsData?.pages.flatMap(page => page.workouts) || [];

  const isLoading = categoriesLoading || workoutsLoading;
  const isError = categoriesError || workoutsError;
  const errorMessage = categoriesErr?.message || workoutsErr?.message;
  const isFetchingNext = isFetchingNextCategories || isFetchingNextWorkouts;

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const currentLoaderRef = loaderRef.current;
    
    if (!currentLoaderRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting && !isFetchingNext) {
          // Fetch next page for both categories and workouts
          if (hasNextCategories) fetchNextCategories();
          if (hasNextWorkouts) fetchNextWorkouts();
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );

    observer.observe(currentLoaderRef);

    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [hasNextCategories, hasNextWorkouts, isFetchingNext, fetchNextCategories, fetchNextWorkouts]);

  const workoutsByCategory = groupWorkoutsByCategory(allWorkouts);
  const hasAnyWorkouts = Object.keys(workoutsByCategory).length > 0;

  if (isLoading && allCategories.length === 0 && allWorkouts.length === 0) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#c8f000]" />
          <p className="mt-4 text-gray-500">Loading videos...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-white">
        <div className="text-center bg-red-50 p-6 rounded-2xl max-w-md">
          <p className="text-red-600 font-semibold mb-2">
            Something went wrong
          </p>
          <p className="text-red-400 text-sm mb-4">{errorMessage}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#c8f000] text-black font-semibold rounded-full hover:opacity-90 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white font-sans">
      {allCategories.map((category) => {
        const categoryWorkouts = workoutsByCategory[category._id] || [];
        if (categoryWorkouts.length === 0) return null;

        return (
          <section key={category._id} className="mb-10">
            <div className="flex items-center justify-between px-6 py-4 mb-4">
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                {category.name}
              </h2>
            </div>

            <div
              className="flex gap-4 overflow-x-auto px-6 pb-4"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {categoryWorkouts.map((workout) => (
                <Workout
                  key={workout._id}
                  workout={workout}
                  categoryName={category.name}
                />
              ))}
            </div>
          </section>
        );
      })}

      {/* Loading indicator and intersection observer trigger */}
      {(hasNextCategories || hasNextWorkouts) && (
        <div 
          ref={loaderRef}
          className="flex justify-center items-center py-8"
        >
          {isFetchingNext ? (
            <div className="flex flex-col items-center gap-2">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#c8f000]" />
              <p className="text-sm text-gray-500">Loading more videos...</p>
            </div>
          ) : (
            <div className="h-10" /> // Invisible trigger element
          )}
        </div>
      )}

      {allCategories.length > 0 && !hasAnyWorkouts && (
        <div className="text-center py-12 text-gray-400">No videos found.</div>
      )}

      {allCategories.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          No categories found.
        </div>
      )}
    </div>
  );
};

export default WorkoutCategory;